import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import Modal from "@/components/modal/Modal";
import { literals } from "@/constants/literals";
import { useLoader } from "@/store/useLoader";
import { useState } from "react";
import { appToast } from "@/lib/toast";
import noImage from "@/assets/no_available.png";
import { cn } from "@/lib/styles.utils";
import { Icon } from "@/components/icons/Icon";
import { useUserStore } from "@/store/useUser";

const noImageUrl = new URL(noImage, import.meta.url).href;

const DeletePhoto = ({
  onDelete,
  imageURL,
  confirmText,
  disabled,
}: {
  onDelete: () => void;
  imageURL: string;
  confirmText: string;
  disabled?: boolean;
}) => {
  const [error, setError] = useState("");
  const [viewModalConfirm, setViewModalConfirm] = useState(false);
  const { upLoader, downLoader } = useLoader();
  const { isSupervisor } = useUserStore();

  const handleOnClose = () => {
    setError("");
    setViewModalConfirm(false);
  };

  const handleOnOpenModal = () => {
    if (!disabled) {
      setViewModalConfirm(true);
    }
  };

  const handleOnConfirm = async () => {
    if (disabled) return;
    try {
      upLoader();
      setError("");
      await onDelete();

      setViewModalConfirm(false);

      appToast.success(literals.success, literals.requestCompleted);
    } catch (error) {
      const e = error as Error;
      const msg = e.message ?? literals.errorDeletingPhoto;

      setError(msg);

      appToast.error(literals.aProblem, msg);
    } finally {
      downLoader();
    }
  };

  return (
    <>
      {viewModalConfirm && (
        <Modal
          opened={viewModalConfirm}
          title={literals.deletePhoto}
          onClose={handleOnClose}
        >
          <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-1">
            <div className="space-y-4">
              <p className="text-sm font-ib-normal text-[#666666]">
                {confirmText}
              </p>
              <div
                data-testid="image-load"
                className="py-2 max-h-[350px]"
              ></div>
              <img
                data-testid="remove-photo-detail"
                id="remove-photo-detail"
                src={imageURL}
                alt="Foto"
                className="block max-h-[350px] max-w-full w-auto object-contain rounde mx-auto"
                onError={(e) => {
                  // Si la URL existe pero la descarga falla (S3 403, 404, etc.)
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = noImageUrl;
                }}
              />
            </div>

            <div className="grid gap-4">
              {error && (
                <p className="text-sm font-ib-normal text-red-600">{error}</p>
              )}
              <PrimaryButton
                dataTestid="confirm-delete-photo"
                label={
                  isSupervisor
                    ? literals.deletePhoto
                    : literals.sendDeleteRequest
                }
                colour={"red"}
                onClick={handleOnConfirm}
              />
              <SecondaryButton
                dataTestid="cancel-delete-photo"
                label={literals.cancel}
                colour={"red"}
                onClick={handleOnClose}
              />
            </div>
          </div>
        </Modal>
      )}
      <button
        disabled={disabled}
        data-testid={"delete-photo"}
        type="button"
        role="menuitem"
        onClick={handleOnOpenModal}
        className={cn(
          "flex w-full items-center gap-3 border-b border-gray-200 px-5 py-4 text-left text-[15px]",
          "last:border-b-0",
          disabled
            ? "cursor-not-allowed text-gray-400"
            : "text-gray-800 hover:bg-gray-50",
        )}
      >
        <Icon name="remove" className="" />
        <span>{isSupervisor ? literals.delete : literals.requestDelete}</span>
      </button>
    </>
  );
};

export default DeletePhoto;
